import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { EditorialPerInitiative } from "../../AddActivity/types";
import { mapWizardDataToPathInputForm } from "../payload/mapWizardDataToPathInputForm";
import { buildPathPayload } from "../payload/buildPathPayload";
import { PathWizardData } from "../types";

interface UseEditPathV2Args {
  corporateId: string;
  loId: number;
  initialInitiativeIds: number[];
  initialEditorial: Record<number, EditorialPerInitiative>;
}

interface EditResult {
  loId: number;
  initiativesAdded: number;
  initiativesRemoved: number;
  topicsAdded: number;
  topicsRemoved: number;
  categoriesAdded: number;
  categoriesRemoved: number;
}

interface UseEditPathV2Return {
  submit: (data: PathWizardData) => Promise<EditResult>;
  isSubmitting: boolean;
  error: string | null;
}

const diff = (
  prev: number[],
  next: number[],
): { added: number[]; removed: number[] } => {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  return {
    added: next.filter((n) => !prevSet.has(n)),
    removed: prev.filter((n) => !nextSet.has(n)),
  };
};

const EMPTY_EDITORIAL: EditorialPerInitiative = {
  selected_carousel_ids: [],
  selected_topic_ids: [],
  selected_skill_ids: [],
};

export const useEditPathV2 = ({
  corporateId,
  loId,
  initialInitiativeIds,
  initialEditorial,
}: UseEditPathV2Args): UseEditPathV2Return => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noAutoRevalidate = { revalidate: false } as const;

  const pathUrl = `${ServicesURL["learning-catalogue"]}/${corporateId}/learning-objects/paths/${loId}`;
  const { trigger: triggerEditPath } = useSWRMutation(
    pathUrl,
    (url: string, { arg }: { arg: unknown }) =>
      fetcher(url, MethodType.PUT, arg),
    noAutoRevalidate,
  );

  const initBaseUrl = `${ServicesURL.catalogDomainAssociation}/${corporateId}/learning-objects/${loId}/initiatives`;

  const { trigger: triggerInitiativeAssoc } = useSWRMutation(
    initBaseUrl,
    (url: string, { arg }: { arg: { initiativeId: number } }) =>
      fetcher(url, MethodType.PUT, { id: arg.initiativeId }),
    noAutoRevalidate,
  );

  const { trigger: triggerInitiativeDisassoc } = useSWRMutation(
    `${initBaseUrl}:DELETE`,
    (_url: string, { arg }: { arg: { initiativeId: number } }) => {
      const realUrl = `${initBaseUrl}/${arg.initiativeId}`;
      return fetcher(realUrl, MethodType.DELETE);
    },
    noAutoRevalidate,
  );

  const { trigger: triggerTopicAssoc } = useSWRMutation(
    ServicesURL.catalogTopicAssociation,
    (
      _url: string,
      { arg }: { arg: { initiativeId: number; topicId: number } },
    ) => {
      const realUrl = ServicesURL.catalogTopicAssociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(loId))
        .replace(":initiativeId", String(arg.initiativeId));
      return fetcher(realUrl, MethodType.PUT, { id: arg.topicId });
    },
    noAutoRevalidate,
  );

  const { trigger: triggerTopicDisassoc } = useSWRMutation(
    ServicesURL.catalogTopicDisassociation,
    (
      _url: string,
      { arg }: { arg: { initiativeId: number; topicId: number } },
    ) => {
      const realUrl = ServicesURL.catalogTopicDisassociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(loId))
        .replace(":initiativeId", String(arg.initiativeId))
        .replace(":idTopic", String(arg.topicId));
      return fetcher(realUrl, MethodType.DELETE);
    },
    noAutoRevalidate,
  );

  const { trigger: triggerCategoryAssoc } = useSWRMutation(
    ServicesURL.catalogCategoryAssociation,
    (
      _url: string,
      { arg }: { arg: { initiativeId: number; categoryId: number } },
    ) => {
      const realUrl = ServicesURL.catalogCategoryAssociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(loId))
        .replace(":initiativeId", String(arg.initiativeId));
      return fetcher(realUrl, MethodType.PUT, { id: arg.categoryId });
    },
    noAutoRevalidate,
  );

  const { trigger: triggerCategoryDisassoc } = useSWRMutation(
    ServicesURL.catalogCategoryDisassociation,
    (
      _url: string,
      { arg }: { arg: { initiativeId: number; categoryId: number } },
    ) => {
      const realUrl = ServicesURL.catalogCategoryDisassociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(loId))
        .replace(":initiativeId", String(arg.initiativeId))
        .replace(":idCategory", String(arg.categoryId));
      return fetcher(realUrl, MethodType.DELETE);
    },
    noAutoRevalidate,
  );

  const submit = async (data: PathWizardData): Promise<EditResult> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const inputForm = mapWizardDataToPathInputForm(data);
      const payload = buildPathPayload(inputForm);
      await triggerEditPath(payload);

      const nextInitiativeIds = data.selected_initiatives.map((it) =>
        Number(it.id),
      );
      const { added: initAdded, removed: initRemoved } = diff(
        initialInitiativeIds,
        nextInitiativeIds,
      );

      for (const initId of initRemoved) {
        await triggerInitiativeDisassoc({ initiativeId: initId });
      }
      for (const initId of initAdded) {
        await triggerInitiativeAssoc({ initiativeId: initId });
      }

      const keptOrAdded = nextInitiativeIds;
      let topicsAdded = 0;
      let topicsRemoved = 0;
      let categoriesAdded = 0;
      let categoriesRemoved = 0;

      for (const initId of keptOrAdded) {
        const prev = initialEditorial[initId] ?? EMPTY_EDITORIAL;
        const next = data.editorial_per_initiative[initId] ?? EMPTY_EDITORIAL;

        const topicsDiff = diff(
          prev.selected_topic_ids,
          next.selected_topic_ids,
        );
        for (const topicId of topicsDiff.removed) {
          await triggerTopicDisassoc({ initiativeId: initId, topicId });
          topicsRemoved += 1;
        }
        for (const topicId of topicsDiff.added) {
          await triggerTopicAssoc({ initiativeId: initId, topicId });
          topicsAdded += 1;
        }

        const catsDiff = diff(
          prev.selected_carousel_ids,
          next.selected_carousel_ids,
        );
        for (const catId of catsDiff.removed) {
          await triggerCategoryDisassoc({
            initiativeId: initId,
            categoryId: catId,
          });
          categoriesRemoved += 1;
        }
        for (const catId of catsDiff.added) {
          await triggerCategoryAssoc({
            initiativeId: initId,
            categoryId: catId,
          });
          categoriesAdded += 1;
        }
      }

      return {
        loId,
        initiativesAdded: initAdded.length,
        initiativesRemoved: initRemoved.length,
        topicsAdded,
        topicsRemoved,
        categoriesAdded,
        categoriesRemoved,
      };
    } catch (e: any) {
      const msg = e?.message || "Submit edit percorso fallito";
      setError(msg);
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting, error };
};
